/**
 * Sanitizes raw markdown content before it reaches the markdown-it block tokenizer.
 *
 * **Testing note:** Table-row outdent (2+ space–indented `|` lines) is
 * intentionally not applied here so callers can reproduce table-related parser
 * behavior. Only fence handling runs.
 *
 * ## Code fences indented 4+ spaces (inside nested list items)
 *
 * markdown-it's `fence` block rule only recognizes fences with 0–3 spaces of
 * leading indentation. Fences indented with 4+ spaces (common inside nested
 * list items like "     ```jql") are treated as indented code continuation,
 * meaning the opening fence is never paired with its closing fence. The block
 * parser then tries every rule against the remaining content until EOF →
 * effectively infinite loop → OOM.
 *
 * ## Fix
 *
 * Outdent opening/closing fence delimiters when they were indented with 4+
 * spaces so the parser recognizes fenced code blocks. Lines inside the fence
 * are unchanged.
 */
export function sanitizeMarkdown(content: string): string {
	const lines = content.split('\n');
	const result: string[] = [];
	let inFence = false;
	let fenceIndent = 0;

	for (const line of lines) {
		const stripped = line.trimStart();
		const indent = line.length - stripped.length;

		if (!inFence && stripped.startsWith('```')) {
			if (indent >= 4) {
				// Outdent deeply-indented opening fence to column 0 so the parser
				// recognizes it as a fenced code block
				result.push(stripped);
				inFence = true;
				fenceIndent = indent;
			} else {
				result.push(line);
				inFence = true;
				fenceIndent = indent;
			}
		} else if (inFence && stripped.startsWith('```')) {
			if (indent >= 4 && fenceIndent >= 4) {
				// Outdent matching closing fence
				result.push(stripped);
			} else {
				result.push(line);
			}
			inFence = false;
			fenceIndent = 0;
		} else {
			result.push(line);
		}
	}

	return result.join('\n');
}
